import { useEffect, useRef } from "react";
import {
  X,
  FileText,
  CalendarClock,
  Quote,
  Info,
  Tag,
  Clock,
  Sparkles,
  Image,
  MessageSquare,
} from "lucide-react";

const gradientPrimary =
  "linear-gradient(rgb(85, 37, 235) 0%, rgb(26, 14, 65) 100%)";

export const EmailMessageViewModal = ({ isOpen, onClose, data }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-black/60 via-black/40 to-black/60 backdrop-blur-md flex justify-center items-center px-4 py-4 overflow-y-auto">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl animate-scale-in shadow-2xl border border-gray-100 w-full max-w-3xl max-h-[90vh] purple-scroll overflow-auto relative flex flex-col"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-64 h-64 bg-gradient-to-br from-blue-400/8 to-purple-400/8 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-gradient-to-tr from-indigo-400/8 to-pink-400/8 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative ">
          <div
            className="p-4 rounded-[10px]"
            style={{ background: gradientPrimary }}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                  {data.type === "media" ? (
                    <Image className="w-5 h-5 text-white" />
                  ) : (
                    <FileText className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white mb-0.5 tracking-tight">
                    {data.type === "media"
                      ? "Media Template"
                      : "Message Template"}
                  </h1>
                  <p className="text-white/80 text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Template Overview & Details
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-white transition-all duration-200 hover:scale-110"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="relative px-4 py-4 space-y-4">
          {/* Template Information Section */}

          {/* Content Section - Dynamic based on type */}
          {data.sampleMessage && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg">
                  <MessageSquare className="w-4 h-4 text-orange-600" />
                </div>
                <h2 className="text-base font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Sample Message Preview
                </h2>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>

                <div
                  className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 rounded-xl border border-indigo-200 shadow-sm
        prose-new max-w-none
        [&_h1]:text-xl [&_h1]:text-center
        [&_p]:text-center
        [&_img.editor-image]:mx-auto [&_img.editor-image]:rounded-xl [&_img.editor-image]:max-w-full
        [&_button.editor-button]:bg-[#4F22D8] [&_button.editor-button]:text-white
        [&_button.editor-button]:px-4 [&_button.editor-button]:py-2 [&_button.editor-button]:rounded-md
        [&_button.editor-button]:text-sm [&_button.editor-button]:font-medium [&_button.editor-button]:block [&_button.editor-button]:mx-auto"
                  dangerouslySetInnerHTML={{ __html: data.sampleMessage }}
                />
              </div>
            </section>
          )}

          {/* Placeholders Section - Only show if placeholders exist */}
          {data.placeholders && data.placeholders.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-gradient-to-br from-pink-100 to-rose-100 rounded-lg">
                  <Tag className="w-4 h-4 text-pink-600" />
                </div>
                <h2 className="text-base font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Dynamic Placeholders
                </h2>
              </div>

              <div className="flex flex-wrap gap-2">
                {data.placeholders.map((placeholder, i) => (
                  <span
                    key={i}
                    className="group relative px-3 py-1 text-white font-semibold rounded-full shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 cursor-default"
                    style={{ background: gradientPrimary }}
                  >
                    <span className="relative z-10 text-xs font-mono">
                      {placeholder}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Timestamps Section */}
          <section className="pt-3 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  label: "Created",
                  value: new Date(data.createdAt).toLocaleString(),
                  icon: <CalendarClock className="w-4 h-4" />,
                  gradient: "from-green to-emerald-500",
                },
                {
                  label: "Last Updated",
                  value: new Date(data.updatedAt).toLocaleString(),
                  icon: <Clock className="w-4 h-4" />,
                  gradient: "from-blue-500 to-cyan-500",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 shadow-sm"
                >
                  <div
                    className={`p-2 bg-gradient-to-br ${item.gradient} rounded-lg text-white shadow-sm`}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {item.label}
                    </p>
                    <p className="text-sm font-bold text-gray-800 mt-0.5">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default EmailMessageViewModal;
